import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { toast } from 'sonner';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  FileText, Plus, Search, Trash2, ArrowLeft, Send, CheckCircle2, XCircle,
  Loader2, DollarSign, Package, User, Building2, Briefcase, RefreshCw,
  Edit, Eye, Download, ShoppingCart, ArrowRight, Clock, AlertTriangle,
  History, Printer, Save
} from 'lucide-react';
import { salesQuotesAPI, productsAPI, contactsAPI, companiesAPI, dealsAPI } from '../api';

const statusColors = {
  draft: 'bg-slate-100 text-slate-800',
  pending: 'bg-yellow-100 text-yellow-800',
  sent: 'bg-blue-100 text-blue-800',
  viewed: 'bg-purple-100 text-purple-800',
  accepted: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  expired: 'bg-gray-100 text-gray-800',
  converted: 'bg-emerald-100 text-emerald-800'
};

export const SalesQuotesPage = () => {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Data state
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({});
  const [products, setProducts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [deals, setDeals] = useState([]);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [quoteHistory, setQuoteHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [statusChangeNote, setStatusChangeNote] = useState('');
  const [newStatus, setNewStatus] = useState('');
  
  // Form state
  const [quoteForm, setQuoteForm] = useState({
    title: '',
    description: '',
    contact_id: '',
    company_id: '',
    deal_id: '',
    discount_percent: 0,
    tax_percent: 0,
    payment_terms: 'Net 30',
    delivery_terms: '',
    notes: '',
    valid_days: 30,
    line_items: []
  });
  
  // Line item form
  const [lineItemForm, setLineItemForm] = useState({
    product_id: '',
    quantity: 1,
    discount_percent: 0
  });

  useEffect(() => {
    fetchAllData();
  }, [statusFilter]);

  const fetchAllData = async () => {
    try {
      setRefreshing(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;
      
      const [quotesRes, productsRes, contactsRes, companiesRes, dealsRes] = await Promise.all([
        salesQuotesAPI.getAll(params),
        productsAPI.getAll(),
        contactsAPI.getAll({ limit: 500 }),
        companiesAPI.getAll(),
        dealsAPI.getAll({ limit: 500 })
      ]);
      
      setQuotes(quotesRes.data.quotes || []);
      setStats(quotesRes.data.stats || {});
      setProducts(productsRes.data || []);
      setContacts(contactsRes.data?.contacts || contactsRes.data || []);
      setCompanies(companiesRes.data || []);
      setDeals(dealsRes.data?.deals || dealsRes.data || []);
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load quotes');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchAllData();
  };

  const handleAddLineItem = () => {
    if (!lineItemForm.product_id) {
      toast.error('Please select a product');
      return;
    }
    
    const product = products.find(p => p.id === lineItemForm.product_id);
    if (!product) return;
    
    const newItem = {
      product_id: product.id,
      product_name: product.name,
      product_sku: product.sku,
      quantity: lineItemForm.quantity,
      unit_price: product.price,
      discount_percent: lineItemForm.discount_percent,
      subtotal: product.price * lineItemForm.quantity
    };
    
    setQuoteForm(prev => ({
      ...prev,
      line_items: [...prev.line_items, newItem]
    }));
    
    setLineItemForm({
      product_id: '',
      quantity: 1,
      discount_percent: 0
    });
  };

  const handleRemoveLineItem = (index) => {
    setQuoteForm(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotals = () => {
    const subtotal = quoteForm.line_items.reduce((sum, item) => {
      const lineSubtotal = item.unit_price * item.quantity;
      const discount = lineSubtotal * (item.discount_percent / 100);
      return sum + (lineSubtotal - discount);
    }, 0);
    
    const discountAmount = subtotal * (quoteForm.discount_percent / 100);
    const afterDiscount = subtotal - discountAmount;
    const taxAmount = afterDiscount * (quoteForm.tax_percent / 100);
    const total = afterDiscount + taxAmount;
    
    return { subtotal, discountAmount, taxAmount, total };
  };

  const handleCreateQuote = async () => {
    if (!quoteForm.title) {
      toast.error('Quote title is required');
      return;
    }
    
    if (quoteForm.line_items.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }
    
    try {
      setCreating(true);
      const response = await salesQuotesAPI.create(quoteForm);
      toast.success('Quote created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchAllData();
    } catch (error) {
      console.error('Failed to create quote:', error);
      toast.error('Failed to create quote');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setQuoteForm({
      title: '',
      description: '',
      contact_id: '',
      company_id: '',
      deal_id: '',
      discount_percent: 0,
      tax_percent: 0,
      payment_terms: 'Net 30',
      delivery_terms: '',
      notes: '',
      valid_days: 30,
      line_items: []
    });
    setLineItemForm({
      product_id: '',
      quantity: 1,
      discount_percent: 0
    });
  };

  const handleSendQuote = async (quoteId) => {
    try {
      await salesQuotesAPI.send(quoteId);
      toast.success('Quote marked as sent');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to send quote');
    }
  };

  const handleAcceptQuote = async (quoteId) => {
    try {
      await salesQuotesAPI.accept(quoteId);
      toast.success('Quote accepted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to accept quote');
    }
  };

  const handleRejectQuote = async (quoteId) => {
    const reason = window.prompt('Enter rejection reason (optional):');
    try {
      await salesQuotesAPI.reject(quoteId, reason);
      toast.success('Quote rejected');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to reject quote');
    }
  };

  const handleConvertToOrder = async (quoteId) => {
    if (!window.confirm('Convert this quote to an order?')) return;
    
    try {
      const response = await salesQuotesAPI.convertToOrder(quoteId);
      toast.success(`Order ${response.data.order_number} created`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to convert quote to order');
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm('Delete this quote?')) return;
    
    try {
      await salesQuotesAPI.delete(quoteId);
      toast.success('Quote deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete quote');
    }
  };

  // Load quote history
  const loadQuoteHistory = async (quoteId) => {
    try {
      setHistoryLoading(true);
      const response = await salesQuotesAPI.getHistory(quoteId);
      setQuoteHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to load history:', error);
      setQuoteHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Open edit dialog
  const openEditDialog = (quote) => {
    setEditForm({
      title: quote.title || '',
      description: quote.description || '',
      contact_id: quote.contact_id || '',
      company_id: quote.company_id || '',
      deal_id: quote.deal_id || '',
      discount_percent: quote.discount_percent || 0,
      tax_percent: quote.tax_percent || 0,
      payment_terms: quote.payment_terms || 'Net 30',
      delivery_terms: quote.delivery_terms || '',
      notes: quote.notes || '',
      terms_and_conditions: quote.terms_and_conditions || '',
      change_notes: ''
    });
    setSelectedQuote(quote);
    setShowEditDialog(true);
  };

  // Save quote edits
  const handleSaveEdit = async () => {
    if (!selectedQuote) return;
    
    try {
      await salesQuotesAPI.update(selectedQuote.id, editForm);
      toast.success('Quote updated');
      setShowEditDialog(false);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update quote');
    }
  };

  // Update quote status with notes
  const handleStatusChange = async () => {
    if (!selectedQuote || !newStatus) return;
    
    try {
      await salesQuotesAPI.updateStatus(selectedQuote.id, {
        status: newStatus,
        change_notes: statusChangeNote
      });
      toast.success('Status updated');
      setShowStatusDialog(false);
      setStatusChangeNote('');
      setNewStatus('');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Generate PDF
  const generatePDF = async (quote) => {
    try {
      const doc = new jsPDF();
      
      // Header
      doc.setFontSize(20);
      doc.setTextColor(40, 40, 40);
      doc.text('QUOTE', 105, 20, { align: 'center' });
      
      doc.setFontSize(12);
      doc.text(`Quote #: ${quote.quote_number}`, 14, 35);
      doc.text(`Date: ${format(new Date(quote.created_at), 'MMM d, yyyy')}`, 14, 42);
      doc.text(`Valid Until: ${quote.valid_until ? format(new Date(quote.valid_until), 'MMM d, yyyy') : 'N/A'}`, 14, 49);
      doc.text(`Status: ${quote.status.toUpperCase()}`, 14, 56);
      
      // Customer info
      doc.setFontSize(11);
      doc.text('Bill To:', 14, 70);
      doc.setFontSize(10);
      doc.text(quote.contact_name || quote.company_name || 'N/A', 14, 77);
      if (quote.company_name && quote.contact_name) {
        doc.text(quote.company_name, 14, 83);
      }
      
      // Title
      doc.setFontSize(14);
      doc.text(quote.title, 14, 100);
      if (quote.description) {
        doc.setFontSize(10);
        doc.text(quote.description, 14, 108);
      }
      
      // Line items table
      const tableData = (quote.line_items || []).map(item => [
        item.product_name || 'Product',
        item.quantity.toString(),
        `$${(item.unit_price || 0).toFixed(2)}`,
        `${item.discount_percent || 0}%`,
        `$${(item.total || 0).toFixed(2)}`
      ]);
      
      doc.autoTable({
        startY: 115,
        head: [['Item', 'Qty', 'Unit Price', 'Discount', 'Total']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [66, 66, 66] },
        margin: { left: 14, right: 14 }
      });
      
      // Totals
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(10);
      doc.text(`Subtotal: $${(quote.subtotal || 0).toFixed(2)}`, 140, finalY, { align: 'left' });
      if (quote.discount_amount > 0) {
        doc.text(`Discount: -$${quote.discount_amount.toFixed(2)}`, 140, finalY + 7, { align: 'left' });
      }
      if (quote.tax_amount > 0) {
        doc.text(`Tax: $${quote.tax_amount.toFixed(2)}`, 140, finalY + 14, { align: 'left' });
      }
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(`Total: $${(quote.total || 0).toFixed(2)}`, 140, finalY + 24, { align: 'left' });
      
      // Terms
      if (quote.payment_terms || quote.notes) {
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        const termsY = finalY + 40;
        if (quote.payment_terms) {
          doc.text(`Payment Terms: ${quote.payment_terms}`, 14, termsY);
        }
        if (quote.notes) {
          doc.text('Notes:', 14, termsY + 10);
          const splitNotes = doc.splitTextToSize(quote.notes, 180);
          doc.text(splitNotes, 14, termsY + 17);
        }
      }
      
      // Footer
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text('Generated by UnifiCRM', 105, 285, { align: 'center' });
      
      // Save
      doc.save(`Quote_${quote.quote_number}.pdf`);
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sales-quotes-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="h-6 w-6" />
            Sales Quotes
          </h1>
          <p className="text-muted-foreground">
            Create and manage sales quotes for customers
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchAllData} disabled={refreshing}>
            {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-quote-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Quote
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="cursor-pointer hover:bg-slate-50" onClick={() => setStatusFilter('all')}>
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-2xl font-bold">{stats.total || 0}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-slate-50" onClick={() => setStatusFilter('draft')}>
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-2xl font-bold text-slate-600">{stats.draft || 0}</p>
            <p className="text-xs text-muted-foreground">Draft</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-slate-50" onClick={() => setStatusFilter('sent')}>
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.sent || 0}</p>
            <p className="text-xs text-muted-foreground">Sent</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-slate-50" onClick={() => setStatusFilter('accepted')}>
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.accepted || 0}</p>
            <p className="text-xs text-muted-foreground">Accepted</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-slate-50" onClick={() => setStatusFilter('rejected')}>
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-2xl font-bold text-red-600">{stats.rejected || 0}</p>
            <p className="text-xs text-muted-foreground">Rejected</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-slate-50" onClick={() => setStatusFilter('converted')}>
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{stats.converted || 0}</p>
            <p className="text-xs text-muted-foreground">Converted</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-center">
            <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-[200px]">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search quotes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button type="submit" variant="outline">Search</Button>
            </form>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Quotes List */}
      <Card>
        <CardContent className="p-0">
          {quotes.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No quotes found</p>
              <Button variant="outline" className="mt-4" onClick={() => setShowCreateDialog(true)}>
                Create First Quote
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quote #</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Valid Until</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quotes.map((quote) => (
                  <TableRow key={quote.id} data-testid={`quote-row-${quote.id}`}>
                    <TableCell className="font-mono text-sm">{quote.quote_number}</TableCell>
                    <TableCell className="font-medium">{quote.title}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        {quote.contact_name && (
                          <span className="text-sm flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {quote.contact_name}
                          </span>
                        )}
                        {quote.company_name && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {quote.company_name}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[quote.status]}>
                        {quote.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      ${quote.total?.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {quote.valid_until && (
                        <span className={`text-sm ${new Date(quote.valid_until) < new Date() ? 'text-red-500' : 'text-muted-foreground'}`}>
                          {format(new Date(quote.valid_until), 'MMM d, yyyy')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => { setSelectedQuote(quote); setShowDetailDialog(true); }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {quote.status === 'draft' && (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => handleSendQuote(quote.id)}>
                              <Send className="h-4 w-4 text-blue-600" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteQuote(quote.id)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </>
                        )}
                        {quote.status === 'sent' && (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => handleAcceptQuote(quote.id)}>
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleRejectQuote(quote.id)}>
                              <XCircle className="h-4 w-4 text-red-500" />
                            </Button>
                          </>
                        )}
                        {quote.status === 'accepted' && (
                          <Button variant="ghost" size="icon" onClick={() => handleConvertToOrder(quote.id)}>
                            <ShoppingCart className="h-4 w-4 text-emerald-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Quote Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Sales Quote</DialogTitle>
            <DialogDescription>
              Create a new quote for a customer with products and pricing
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="items">Line Items ({quoteForm.line_items.length})</TabsTrigger>
              <TabsTrigger value="terms">Terms & Notes</TabsTrigger>
            </TabsList>
            
            <TabsContent value="details" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="title">Quote Title *</Label>
                  <Input
                    id="title"
                    value={quoteForm.title}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Annual Service Agreement for Acme Corp"
                    data-testid="quote-title-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={quoteForm.description}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of this quote..."
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Contact</Label>
                  <Select 
                    value={quoteForm.contact_id || 'none'} 
                    onValueChange={(v) => setQuoteForm(prev => ({ ...prev, contact_id: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select contact" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No contact</SelectItem>
                      {contacts.map(contact => (
                        <SelectItem key={contact.id} value={contact.id}>
                          {contact.first_name} {contact.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Company</Label>
                  <Select 
                    value={quoteForm.company_id || 'none'} 
                    onValueChange={(v) => setQuoteForm(prev => ({ ...prev, company_id: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select company" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No company</SelectItem>
                      {companies.map(company => (
                        <SelectItem key={company.id} value={company.id}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Link to Deal (optional)</Label>
                  <Select 
                    value={quoteForm.deal_id || 'none'} 
                    onValueChange={(v) => setQuoteForm(prev => ({ ...prev, deal_id: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select deal" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No deal</SelectItem>
                      {deals.map(deal => (
                        <SelectItem key={deal.id} value={deal.id}>
                          {deal.title} (${deal.value?.toLocaleString()})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="valid_days">Valid For (days)</Label>
                  <Input
                    id="valid_days"
                    type="number"
                    value={quoteForm.valid_days}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, valid_days: parseInt(e.target.value) || 30 }))}
                    min={1}
                    max={365}
                  />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="items" className="space-y-4 mt-4">
              {/* Add Line Item */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Add Product/Service</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4 items-end">
                    <div className="flex-1">
                      <Label>Product/Service</Label>
                      <Select 
                        value={lineItemForm.product_id || 'select'} 
                        onValueChange={(v) => setLineItemForm(prev => ({ ...prev, product_id: v === 'select' ? '' : v }))}
                      >
                        <SelectTrigger data-testid="line-item-product-select">
                          <SelectValue placeholder="Select product" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="select">Select a product...</SelectItem>
                          {products.map(product => (
                            <SelectItem key={product.id} value={product.id}>
                              {product.name} - ${product.price?.toLocaleString()} ({product.product_type})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-24">
                      <Label>Qty</Label>
                      <Input
                        type="number"
                        value={lineItemForm.quantity}
                        onChange={(e) => setLineItemForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                        min={1}
                      />
                    </div>
                    <div className="w-24">
                      <Label>Disc %</Label>
                      <Input
                        type="number"
                        value={lineItemForm.discount_percent}
                        onChange={(e) => setLineItemForm(prev => ({ ...prev, discount_percent: parseFloat(e.target.value) || 0 }))}
                        min={0}
                        max={100}
                      />
                    </div>
                    <Button onClick={handleAddLineItem} data-testid="add-line-item-btn">
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              {/* Line Items List */}
              {quoteForm.line_items.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground border rounded-lg">
                  <Package className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p>No items added yet</p>
                  <p className="text-sm">Add products or services from the dropdown above</p>
                </div>
              ) : (
                <Card>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product/Service</TableHead>
                        <TableHead className="text-right">Unit Price</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Discount</TableHead>
                        <TableHead className="text-right">Subtotal</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {quoteForm.line_items.map((item, index) => {
                        const lineTotal = (item.unit_price * item.quantity) * (1 - item.discount_percent / 100);
                        return (
                          <TableRow key={index}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{item.product_name}</p>
                                <p className="text-xs text-muted-foreground">{item.product_sku}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">${item.unit_price?.toLocaleString()}</TableCell>
                            <TableCell className="text-right">{item.quantity}</TableCell>
                            <TableCell className="text-right">{item.discount_percent}%</TableCell>
                            <TableCell className="text-right font-medium">${lineTotal.toLocaleString()}</TableCell>
                            <TableCell>
                              <Button variant="ghost" size="icon" onClick={() => handleRemoveLineItem(index)}>
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Card>
              )}
              
              {/* Totals */}
              {quoteForm.line_items.length > 0 && (
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex justify-end">
                      <div className="w-64 space-y-2">
                        <div className="flex justify-between">
                          <span>Subtotal:</span>
                          <span>${totals.subtotal.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span>Quote Discount:</span>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              value={quoteForm.discount_percent}
                              onChange={(e) => setQuoteForm(prev => ({ ...prev, discount_percent: parseFloat(e.target.value) || 0 }))}
                              className="w-16 h-8"
                              min={0}
                              max={100}
                            />
                            <span>%</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <span>Tax:</span>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              value={quoteForm.tax_percent}
                              onChange={(e) => setQuoteForm(prev => ({ ...prev, tax_percent: parseFloat(e.target.value) || 0 }))}
                              className="w-16 h-8"
                              min={0}
                              max={100}
                            />
                            <span>%</span>
                          </div>
                        </div>
                        <Separator />
                        <div className="flex justify-between font-bold text-lg">
                          <span>Total:</span>
                          <span>${totals.total.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
            
            <TabsContent value="terms" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="payment_terms">Payment Terms</Label>
                  <Select 
                    value={quoteForm.payment_terms} 
                    onValueChange={(v) => setQuoteForm(prev => ({ ...prev, payment_terms: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Due on Receipt">Due on Receipt</SelectItem>
                      <SelectItem value="Net 15">Net 15</SelectItem>
                      <SelectItem value="Net 30">Net 30</SelectItem>
                      <SelectItem value="Net 45">Net 45</SelectItem>
                      <SelectItem value="Net 60">Net 60</SelectItem>
                      <SelectItem value="50% Upfront">50% Upfront</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="delivery_terms">Delivery Terms</Label>
                  <Input
                    id="delivery_terms"
                    value={quoteForm.delivery_terms}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, delivery_terms: e.target.value }))}
                    placeholder="e.g., 2-3 weeks after approval"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="notes">Notes to Customer</Label>
                  <Textarea
                    id="notes"
                    value={quoteForm.notes}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any special notes or instructions..."
                    rows={3}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleCreateQuote} disabled={creating} data-testid="submit-quote-btn">
              {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quote Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Quote Details</DialogTitle>
            <DialogDescription>
              {selectedQuote?.quote_number} - {selectedQuote?.title}
            </DialogDescription>
          </DialogHeader>
          
          {selectedQuote && (
            <Tabs defaultValue="details">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="history" onClick={() => loadQuoteHistory(selectedQuote.id)}>
                  <History className="h-4 w-4 mr-1" />
                  History
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="details" className="space-y-4">
                <div className="flex items-center justify-between">
                  <Badge className={statusColors[selectedQuote.status]}>
                    {selectedQuote.status}
                  </Badge>
                  <span className="text-2xl font-bold">${selectedQuote.total?.toLocaleString()}</span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Customer</p>
                    <p className="font-medium">{selectedQuote.contact_name || selectedQuote.company_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Valid Until</p>
                    <p className="font-medium">
                      {selectedQuote.valid_until ? format(new Date(selectedQuote.valid_until), 'MMM d, yyyy') : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Created By</p>
                    <p className="font-medium">{selectedQuote.created_by_name}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Created</p>
                    <p className="font-medium">
                      {format(new Date(selectedQuote.created_at), 'MMM d, yyyy')}
                    </p>
                  </div>
                </div>
                
                {selectedQuote.line_items?.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Line Items</p>
                    <ScrollArea className="h-[200px] border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Item</TableHead>
                            <TableHead className="text-right">Qty</TableHead>
                            <TableHead className="text-right">Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedQuote.line_items.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>{item.product_name}</TableCell>
                              <TableCell className="text-right">{item.quantity}</TableCell>
                              <TableCell className="text-right">${item.total?.toLocaleString()}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </div>
                )}
                
                {selectedQuote.notes && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Notes</p>
                    <p className="text-sm p-3 bg-slate-50 rounded-lg">{selectedQuote.notes}</p>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="history" className="space-y-4">
                {historyLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : quoteHistory.length > 0 ? (
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-3">
                      {quoteHistory.map((entry, index) => (
                        <div key={index} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <Badge variant="outline">{entry.action.replace('_', ' ')}</Badge>
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(entry.changed_at), 'MMM d, yyyy HH:mm')}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">By: {entry.changed_by_name}</p>
                          {entry.changes?.length > 0 && (
                            <div className="mt-2 text-xs">
                              {entry.changes.map((change, i) => (
                                <p key={i}>
                                  <span className="font-medium">{change.field}:</span> {String(change.old_value)} → {String(change.new_value)}
                                </p>
                              ))}
                            </div>
                          )}
                          {entry.notes && (
                            <p className="text-sm mt-2 italic">"{entry.notes}"</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No history available</p>
                )}
              </TabsContent>
            </Tabs>
          )}
          
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => generatePDF(selectedQuote)}>
              <Printer className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
            {selectedQuote?.status !== 'converted' && selectedQuote?.status !== 'accepted' && (
              <Button variant="outline" onClick={() => { openEditDialog(selectedQuote); setShowDetailDialog(false); }}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
            <Button variant="outline" onClick={() => { 
              setNewStatus(selectedQuote?.status); 
              setShowStatusDialog(true); 
              setShowDetailDialog(false); 
            }}>
              Change Status
            </Button>
            {selectedQuote?.status === 'draft' && (
              <Button onClick={() => { handleSendQuote(selectedQuote.id); setShowDetailDialog(false); }}>
                <Send className="h-4 w-4 mr-2" />
                Send Quote
              </Button>
            )}
            {selectedQuote?.status === 'accepted' && (
              <Button onClick={() => { handleConvertToOrder(selectedQuote.id); setShowDetailDialog(false); }}>
                <ShoppingCart className="h-4 w-4 mr-2" />
                Convert to Order
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Quote Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Quote</DialogTitle>
            <DialogDescription>
              Modify quote details. Changes will be tracked in history.
            </DialogDescription>
          </DialogHeader>
          
          {editForm && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div>
                <Label>Title</Label>
                <Input 
                  value={editForm.title} 
                  onChange={(e) => setEditForm({...editForm, title: e.target.value})} 
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea 
                  value={editForm.description} 
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})} 
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Discount %</Label>
                  <Input 
                    type="number" 
                    value={editForm.discount_percent} 
                    onChange={(e) => setEditForm({...editForm, discount_percent: parseFloat(e.target.value) || 0})} 
                  />
                </div>
                <div>
                  <Label>Tax %</Label>
                  <Input 
                    type="number" 
                    value={editForm.tax_percent} 
                    onChange={(e) => setEditForm({...editForm, tax_percent: parseFloat(e.target.value) || 0})} 
                  />
                </div>
              </div>
              <div>
                <Label>Payment Terms</Label>
                <Input 
                  value={editForm.payment_terms} 
                  onChange={(e) => setEditForm({...editForm, payment_terms: e.target.value})} 
                />
              </div>
              <div>
                <Label>Delivery Terms</Label>
                <Input 
                  value={editForm.delivery_terms} 
                  onChange={(e) => setEditForm({...editForm, delivery_terms: e.target.value})} 
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea 
                  value={editForm.notes} 
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})} 
                />
              </div>
              <Separator />
              <div>
                <Label>Change Notes (optional)</Label>
                <Textarea 
                  placeholder="Why are you making this change?"
                  value={editForm.change_notes} 
                  onChange={(e) => setEditForm({...editForm, change_notes: e.target.value})} 
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit}>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Change Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change Quote Status</DialogTitle>
            <DialogDescription>
              Update the status of this quote
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="viewed">Viewed</SelectItem>
                  <SelectItem value="accepted">Accepted</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea 
                placeholder="Add notes about this status change..."
                value={statusChangeNote}
                onChange={(e) => setStatusChangeNote(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusDialog(false)}>Cancel</Button>
            <Button onClick={handleStatusChange} disabled={!newStatus}>
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesQuotesPage;
